import { Flex } from 'antd';
import { PrimaryCard } from '../../assets/styles/components/dashboard.styled';
import { Button, CardCta, Divider, Heading, Paragraph } from '../Ant';
import CardData from '../dashboard/cardData';
import { AddSquare, ArrowRight, Briefcase, Calendar, Receipt, ShieldTick, User } from '../icons';
import { useTranslation } from 'react-i18next';
import { IMatter } from '../../interface/matter';
import { MatterStatus } from '../../constants/matter';
import {
  formatDateRange,
  formatter,
  getMatterIconClass,
  getMatterLabel,
} from '../../constants/utilityFunctions';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useTransactionParameters } from '../../context/TransactionParameterContext';
import { useBillableMatterActions } from '../../hooks/useBillableMatterActions';
import { ProductName } from '../../interface/system';
import { useFetchSystemInfo } from '../../hooks/useFetchSystemInfo';
import { MatterBorderColor, MatterTagColor } from '../../constants/invoice';
import NoteIcons from '../common/NoteIcons';
import SkeletonEntityCard from '../common/skeleton/skeletonEntityCard';
import { getViewLabel } from '../../helpers/viewLabelHelper';

const BillableGeneralCard = ({
  billableMatter,
  isLoading = false,
}: {
  billableMatter: IMatter;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { parameters } = useTransactionParameters();
  const baseCurrency = parameters?.currency;
  const { data: systemParams } = useFetchSystemInfo();
  const productName = systemParams?.systemInfo?.productName;
  const isPreliminaryInvoiceCreation = productName !== ProductName.LAKIMIES;
  const { onCreatePreliminaryInvoice, ErrorModal, CreationModal } = useBillableMatterActions();
  const handleGoToCustomer = () => {
    push(
      ROUTES.CUSTOMER_LISTING +
        `/${billableMatter?.principalCompany?.id ?? billableMatter?.principalPrivatePerson?.id}`,
    );
  };
  if (isLoading) {
    return (
      <SkeletonEntityCard
        rows={[1, 1, 1, 1, 1, 2, 2]}
        withSubcontentOnFirstRow
        className="py-10 px-10 h-full"
      />
    );
  }
  return (
    <PrimaryCard
      data-testid="billable-general-card"
      $isDetail
      className={`d-flex flex-col gap-10 h-full conflict-card ${MatterBorderColor[billableMatter?.status]}`}
    >
      <Flex align="center" className="w-full" gap={8}>
        <Flex
          data-testid="billable-general-card-status-chip"
          justify="center"
          align="center"
          className={`status-chip ${MatterTagColor[billableMatter?.status]}`}
        >
          {t(
            billableMatter?.status === MatterStatus.ACTIVE
              ? 'matter:status.active'
              : 'matter:status.closed',
          )}
        </Flex>
        <NoteIcons
          hasImportantNotes={billableMatter?.hasImportantNotes}
          hasImportantRelatedNotes={billableMatter?.hasImportantRelatedNotes}
          hasNotes={billableMatter?.hasNotes}
        />
      </Flex>
      <Divider />
      <Flex vertical gap={10} className="flex-grow">
        <CardData
          type="matter"
          icon={<Briefcase className={getMatterIconClass(billableMatter?.invoiceableType ?? '')} />}
          title={getMatterLabel(t, billableMatter?.invoiceableType)}
          content={
            billableMatter?.principalCompany?.name ??
            billableMatter?.principalPrivatePerson?.fullName ??
            '-'
          }
          subcontent={
            billableMatter?.matterNumber &&
            `${billableMatter?.matterNumber} - ${billableMatter?.subject}`
          }
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-2"
          headingcontent={
            (billableMatter?.principalCompany || billableMatter?.principalPrivatePerson) && (
              <Button
                data-testid="billable-general-card-view-customer"
                type="text"
                icon={<ArrowRight />}
                iconPosition="end"
                className="text-cta"
                onClick={handleGoToCustomer}
              >
                {getViewLabel(t, 'customer:customer')}
              </Button>
            )
          }
        />
        <CardData
          icon={<Calendar />}
          type="transaction"
          title={t('matter:period')}
          content={formatDateRange(billableMatter?.periodStartDate, billableMatter?.periodEndDate)}
        />
        <CardData
          type="matter-owner"
          icon={<User />}
          title={t('matter:owner')}
          content={billableMatter?.ownerUser?.fullName ?? '-'}
        />

        <CardData
          icon={<ShieldTick />}
          title={t('matter:responsiblePerson')}
          type="author"
          content={billableMatter?.responsibleUser?.fullName ?? '-'}
        />
        <CardData
          icon={<Receipt />}
          type="invoicing-contact"
          title={t('matter:invoicingContact')}
          content={billableMatter?.invoicingUser?.fullName ?? '-'}
        />
        <Divider />
        {baseCurrency && baseCurrency !== billableMatter?.currency && (
          <Flex justify="space-between">
            <Paragraph
              data-testid="total-net-amount"
              $fontsize="13px"
              $weight={500}
              $color="title"
              $lineheight="20px"
            >
              {`${t('matter:totalNetAmount')} (${billableMatter?.currency ?? 'EUR'})`}
            </Paragraph>
            <Heading
              data-testid="total-net-amount-value"
              level={6}
              $fontsize="13px"
              $weight={700}
              $color="title"
              $lineheight="20px"
            >
              {formatter.regionalNumberFormat({
                value: billableMatter?.sumOfBillableEntriesInCurrency ?? 0,
                minimumFractionDigits: 2,
              })}
            </Heading>
          </Flex>
        )}
        <Flex justify="space-between">
          <Paragraph
            $fontsize="13px"
            data-testid="total-net-amount"
            $weight={500}
            $color="title"
            $lineheight="20px"
          >
            {`${t('matter:totalNetAmount')} (${baseCurrency ?? 'EUR'})`}
          </Paragraph>
          <Heading
            data-testid="total-net-amount-value"
            level={6}
            $fontsize="13px"
            $weight={700}
            $color="title"
            $lineheight="20px"
          >
            {formatter.regionalNumberFormat({
              value: billableMatter?.sumOfBillableEntries ?? 0,
              minimumFractionDigits: 2,
            })}
          </Heading>
        </Flex>
      </Flex>
      <Flex vertical gap={10}>
        <Divider />
        {isPreliminaryInvoiceCreation && (
          <CardCta
            data-testid="create-preliminary-invoice"
            onClick={() => onCreatePreliminaryInvoice(billableMatter)}
          >
            <AddSquare />
            <Paragraph $fontsize="10px" $color="description" $weight={400} $lineheight="15px">
              {t('matter:createPreliminaryInvoice')}
            </Paragraph>
          </CardCta>
        )}
      </Flex>
      {ErrorModal}
      {CreationModal}
    </PrimaryCard>
  );
};

export default BillableGeneralCard;
